import { DateRange } from '@epicuro-next/ataraxia/models';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import {
  arrayIsEmpty,
  arrayIsNotEmpty,
  getUTCMonthsRange,
  unique,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { OrderType, Prepack } from 'graphql-api/gql-api-interfaces';
import { DateTime } from 'luxon';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../../configurations';
import { DeliveryPeriodType } from '../../models';
import { toDeliveryPeriodFrom } from '../../shared/to-delivery-period-from';
import {
  selectAvailableInventories,
  selectCurrentlyActiveShopDeliveryItems,
  selectCurrentShopCurrentDeliveryPeriodType,
  selectCurrentShopDeliveryPeriods,
  selectDeliveryItemsAcrossShops,
} from '../config-strip.selectors';
import {
  selectDeliveryWindowType,
  selectPrepacks,
} from '../data/config-strip.data.selectors';
import { countItemsTotals } from '../shared/count-items-totals';
import { selectAdvancedMdo } from '../ui/config-strip.ui.selectors';

import { getAvailabilityRange } from './availability-range/get-availability-range';
import { getDateByDeliveryWindowType } from './utils';

const selectCurrentShopFutureDeliveryPeriods = createSelector(
  selectCurrentShopDeliveryPeriods,
  (shopDeliveryPeriods) => shopDeliveryPeriods[DeliveryPeriodType.Future] ?? {},
);

export const selectAvailableFutureInventories = (
  useGlobalInventories: boolean,
) =>
  createSelector(
    selectAvailableInventories(useGlobalInventories),
    (inventories) =>
      inventories.filter(
        (inventory) => inventory.orderType === OrderType.Future,
      ),
  );

export const selectHasAvailableFutureInventories = createSelector(
  selectAvailableFutureInventories(true),
  (inventories) => !!inventories.length,
);

/** getAvailabilityRange does not take into account ESPRIT displayFromDateUtc and displayToDateUtc
 * However it's not needed and not business case as ESPRIT does not have future delivery period calendar
 * where this selector at the end is consumed
 */
export const selectMaxFutureDateRange = (useGlobalInventories: boolean) =>
  createSelector(
    selectAvailableFutureInventories(useGlobalInventories),
    getAvailabilityRange,
  );

export const selectAvailableFutureDeliveryMonths = (
  useGlobalInventories: boolean,
) =>
  createSelector(
    selectAvailableFutureInventories(useGlobalInventories),
    (inventories) => {
      return inventories
        .map((inventory) => {
          // ESPRIT, for instance, uses `displayFromDateUtc`.
          const availabilityFrom =
            inventory.displayFromDateUtc ?? inventory.availableFromDateUtc;
          const availabilityTo =
            inventory.displayToDateUtc ?? inventory.availableToDateUtc;

          return getUTCMonthsRange(availabilityFrom, availabilityTo);
        })
        .flat()
        .filter(unique)
        .sort((a, b) => {
          return Date.parse(a) - Date.parse(b);
        });
    },
  );

const MAX_FUTURE_DELIVERY_MONTHS_TO_SHOW = 24;
/** Returns available FROM NOW future month; filter out "future" month
 * return by BE
 */
export const selectFutureDeliveryMonths = (useGlobalInventories: boolean) =>
  createSelector(
    selectAvailableFutureDeliveryMonths(useGlobalInventories),
    (deliveryMonths): string[] => {
      const nowMonth = DateTime.now().startOf('month');
      return deliveryMonths
        .filter(
          (deliveryMonth) =>
            DateTime.fromISO(deliveryMonth).startOf('month') >= nowMonth,
        )
        .slice(0, MAX_FUTURE_DELIVERY_MONTHS_TO_SHOW);
    },
  );

export const selectGlobalFutureDeliveryMonths =
  selectFutureDeliveryMonths(true);

/** Delivery range from-to of all available inventories */
// useGlobalInventories = true - it will return availability based on inventories of all sizes and prepacks
// useGlobalInventories = false - it will return availability from active variant only
export const selectDeliveryPeriodsDateRange = (useGlobalInventories: boolean) =>
  createSelector(
    selectDeliveryWindowType,
    selectMaxFutureDateRange(useGlobalInventories),
    selectFutureDeliveryMonths(useGlobalInventories),
    (deliveryWindowType, inventoryDateRange, months): DateRange | null => {
      if (arrayIsEmpty(months) || !inventoryDateRange) {
        return null;
      }

      return {
        from: getDateByDeliveryWindowType(
          deliveryWindowType,
          inventoryDateRange.from,
          months[0],
        ),
        to: inventoryDateRange.to,
      };
    },
  );

export const selectActiveVariantDeliveryPeriodsRange =
  selectDeliveryPeriodsDateRange(false);
export const selectGlobalDeliveryPeriodsRange =
  selectDeliveryPeriodsDateRange(true);
export const selectConfigStripDatePickerRange = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.ActiveVariantDateRangeEnabled,
    ConfigStripStateConfigurations
  >(
    FEATURE_NAME,
    ConfigStripStateConfigurationKeys.ActiveVariantDateRangeEnabled,
    true,
  ),
  selectActiveVariantDeliveryPeriodsRange,
  selectGlobalDeliveryPeriodsRange,
  (useActiveVariant, variantRange, globalRange) => {
    return useActiveVariant ? variantRange : globalRange;
  },
);

export const selectConfigStripInStoreDate = createSelector(
  selectConfigStripDatePickerRange,
  (variantRange) =>
    variantRange?.from ? toDeliveryPeriodFrom(variantRange.from) : undefined,
);

/** Returns FUTURE delivery periods */
export const selectFutureDeliveryPeriods = createSelector(
  selectCurrentShopFutureDeliveryPeriods,
  (futureDeliveryPeriods) =>
    [...(futureDeliveryPeriods.deliveryPeriods ?? [])].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    ),
);

export const selectHasFutureDeliveryPeriods = createSelector(
  selectFutureDeliveryPeriods,
  arrayIsNotEmpty,
);

export const selectHasNoFutureDeliveryPeriods = createSelector(
  selectFutureDeliveryPeriods,
  arrayIsEmpty,
);

/** Returns FUTURE disabled delivery periods */
export const selectFutureDisabledDeliveryPeriods = createSelector(
  selectCurrentShopFutureDeliveryPeriods,
  (futureDeliveryPeriods) => {
    return (
      [...(futureDeliveryPeriods.unavailableDeliveryPeriods ?? [])].sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      ) ?? []
    );
  },
);

export const selectSelectedFutureDeliveryPeriod = createSelector(
  selectCurrentShopFutureDeliveryPeriods,
  selectCurrentShopCurrentDeliveryPeriodType,
  (futureDeliveryPeriods, currentSelectedDeliveryPeriodType): string | null => {
    if (currentSelectedDeliveryPeriodType !== DeliveryPeriodType.Future) {
      return null;
    }

    const selected = futureDeliveryPeriods.selectedDeliveryPeriod;

    if (!!selected) {
      return selected;
    }

    return null;
  },
);

export const selectFutureDeliveryItemTotals = createSelector(
  selectPrepacks,
  selectCurrentlyActiveShopDeliveryItems,
  selectDeliveryItemsAcrossShops,
  selectAdvancedMdo,
  (prepacks, deliveryItems, mappedItems, isAdvancedMdo) => {
    const itemsToMap = isAdvancedMdo ? mappedItems : deliveryItems;
    return Object.keys(itemsToMap)
      .filter(
        (periodKey) =>
          itemsToMap[periodKey].orderType === OrderType.Future &&
          itemsToMap[periodKey].deliveryPeriodType ===
            DeliveryPeriodType.Future,
      )
      .reduce((acc, periodKey) => {
        const numberOfItemsInPeriod = countItemsTotals(
          itemsToMap[periodKey].items,
          prepacks as Partial<Prepack>[],
        );

        return {
          ...acc,
          [periodKey]: acc[periodKey]
            ? acc[periodKey] + numberOfItemsInPeriod
            : numberOfItemsInPeriod,
        };
      }, {} as { [period: string]: number });
  },
);
